import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/context/LanguageContext";
import { api, isNotFound } from "@/lib/api";
import { LoadError } from "@/components/LoadError";
import { ISLAND_BY_PATH, islandUrl, tr } from "@/lib/translations";
import { LandingPage } from "@/components/LandingPage";
import { AdminEditButton } from "@/components/AdminEditButton";
import PropertyDetail from "@/pages/PropertyDetail";
import CmsPage from "@/pages/CmsPage";
import RegionPage from "@/pages/RegionPage";
import { mergeBlocks } from "@/lib/landingBlocks";
import { legacyPlaceAlt } from "@/lib/legacyUrls";
import { PlacesBlock, IslandsBlock } from "@/components/LandingBlocks";
import { HighlightsSection } from "@/components/HighlightsSection";
import { ComplexesSection } from "@/components/ComplexesSection";
import { OfflineNotice } from "@/components/OfflineNotice";

const MORE = { de: "Weitere Orte auf {n}", en: "More places on {n}", es: "Más lugares en {n}", nl: "Meer plaatsen op {n}", pl: "Inne miejscowości – {n}", fr: "Autres localités à {n}", it: "Altre località a {n}" };

export default function PlacePage() {
  const { path, place } = useParams();
  const island = ISLAND_BY_PATH[path];
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isProperty, setIsProperty] = useState(false);
  const [isPage, setIsPage] = useState(false);
  const [region, setRegion] = useState(null);

  useEffect(() => {
    setMissing(false); setFailed(false); setData(null); setIsProperty(false); setIsPage(false); setRegion(null);
    // Weiterleitungen imperativ (nicht per <Navigate> im Render – das ließ die Zielseite leer)
    const go = (to, state) => navigate(to + window.location.search, { replace: true, state });
    // Serverfehler (kein 404) in der Kette merken: dann Fehlerhinweis ohne noindex statt „nicht gefunden“
    let transient = false;
    const next = (fn) => (e) => { if (!isNotFound(e)) transient = true; return fn(e); };
    // Nichts unter /insel/<slug> gefunden: bei Serverfehler Hinweis, sonst echte Weiterleitung zur Inselseite (Google sieht Redirect statt noindex)
    const gone = () => (transient ? setFailed(true) : island ? window.location.replace(islandUrl(island) + window.location.search) : setMissing(true));
    // Alt-Adresse „/insel/ferienwohnung-<ort>“ → Ortsseite, aber nur wenn kein Objekt/keine Seite diesen Slug trägt
    const tryLegacy = () => { const alt = legacyPlaceAlt(place); return alt ? api.get(`/places/${alt}`).then((r) => (r.data.island === island ? go(`/${path}/${alt}/`) : gone())).catch(next(gone)) : gone(); };
    // Offline-Objekt: Server liefert im 404 Insel/Ort → dorthin weiterleiten statt Fehlerseite
    const onPropertyError = (e) => { const d = e.response?.data?.detail_raw || e.response?.data?.detail; if (d?.offline && d.island) { go(`${islandUrl(d.island)}${d.place ? `/${d.place}/` : ""}`, { offlineProperty: true }); return; } return api.get(`/pages/${place}`, { params: { island } }).then(() => setIsPage(true)).catch(next(tryLegacy)); };
    // index.html hat für Objekt-URLs schon /properties/<slug>/hero angefragt → Objektseite sofort rendern statt erst Ort/Region zu probieren
    const early = window.__phProp?.then ? window.__phProp.then((h) => (h && h.slug === place ? h : null)).catch(() => null) : Promise.resolve(null);
    early.then((h) => h ? setIsProperty(true)
      : api.get(`/places/${place}`).then((r) => (r.data.island === island ? setData(r.data) : gone()))
        .catch(next(() => api.get(`/regions/${island}/${place}`).then((r) => setRegion(r.data))
          .catch(next(() => api.get(`/properties/${place}`).then(() => setIsProperty(true)).catch(next(onPropertyError)))))));
    window.scrollTo(0, 0);
  }, [place, island]); // eslint-disable-line react-hooks/exhaustive-deps

  if (region) return <RegionPage data={region} />;
  if (isProperty) return <PropertyDetail />;
  if (isPage) return <CmsPage />;
  if (failed) return <LoadError />;
  if (missing) return <NotFound island={island} />;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-32 min-h-screen text-center text-stone-500">{t.property.loading}</div>;
  const c = tr(data.content, lang);
  const islandName = data.island_name[lang] || data.island_name.en || data.island_name.de;
  const std = !data.hero_image && data.hero_fallback?.source === "default";
  const heroProp = [...data.properties].sort((a, b) => (a.ref_number || 0) - (b.ref_number || 0))[0];
  const fb = !data.hero_image && (std || !heroProp?.hero_image) ? data.hero_fallback : null;
  const hero = data.hero_image || (std ? fb.url : heroProp?.hero_image || fb?.url);
  const deSections = (data.content?.de || {}).sections;
  const more = (MORE[lang] || MORE.en).replace("{n}", islandName);

  return (
    <>
    <AdminEditButton kind="place" id={data.slug} />
    <OfflineNotice name={data.name} />
    <LandingPage
      testid="place-page" heroImage={hero} heroPosition={data.hero_image ? data.hero_position : fb ? fb.position : "center"} heroAlt={data.hero_image ? tr(data.hero_alt, lang) : fb ? tr(fb.alt, lang) : ""} badge={`${islandName} · ${t.island.placeBadge}`} content={{ ...c, sections: mergeBlocks(c.sections, deSections) }} properties={data.properties} island={island} place={{ name: data.name, slug: data.slug, island }}
      blocks={{ places: (s) => <PlacesBlock island={island} slugs={s.slugs} exclude={data.slug} heading={more} testId="place-more-places" />, islands: () => <IslandsBlock island={island} /> }}
      afterIntro={<HighlightsSection items={data.highlights} title={t.island.highlights.replace("{n}", data.name)} />}
      afterProperties={<ComplexesSection island={island} place={data.name} heading={t.complex.inPlace.replace("{n}", data.name)} testId="place-complexes" />}
      propertiesTitle={t.island.properties.replace("{n}", data.name)} ctaLabel={t.island.backToIsland.replace("{n}", islandName)} ctaTo={islandUrl(island)} statsName={data.name}
      breadcrumb={[{ name: t.nav.home, to: "/" }, { name: islandName, to: islandUrl(island) }, { name: data.name, to: `${islandUrl(island)}/${data.slug}` }]}
      jsonLd={{ "@context": "https://schema.org", "@type": "TouristDestination", name: data.name, description: c.meta_description, image: hero, url: window.location.href, containedInPlace: { "@type": "Place", name: `${islandName}, Islas Canarias` } }}
    />
    </>
  );
}
